import React from 'react';

import { ButtonGroup } from '@/components/shared/buttons/button-group';
import { Icon } from '@/components/shared/element/icons';
import { Text } from '@/components/shared/element/text';
import { Box } from '@/components/shared/layout/box';
import { Container } from '@/components/shared/layout/container';
import { Flex } from '@/components/shared/layout/flex';
import { Grid } from '@/components/shared/layout/grid';
import { CSS, styled } from '@/stitches.config';
import { IButton } from '@/types/shared';

const StyledHeadline = styled('div', {
  rowGap: '$space-6',
  columnGap: '$space-6',
  variants: {
    layout: {
      'two-column': {},
      'two-row': {
        display: 'grid',

        '@lg': {
          gridTemplateColumns: 'repeat(12, 1fr)',
        },
        '& > *:nth-child(1)': {
          '@lg': {
            gridRow: '1',
            gridColumn: '1 / span 6',
          },
          '@xl': {
            gridColumn: '2 / span 5',
          },
        },
        '& > *:nth-child(2)': {
          '@lg': {
            gridRow: '2',
            gridColumn: '7 / span 6',
          },
          '@xl': {
            gridColumn: '7 / span 5',
          },
        },
        '& > *:only-child': {
          '@lg': {
            gridRow: '1',
            gridColumn: '1 / span 12',
          },
          '@xl': {
            gridColumn: '2 / span 10',
          },
        },
      },
    },
    align: {
      start: {
        '@lg': {
          alignItems: 'flex-start',
        },
      },
      end: {
        '@lg': {
          alignItems: 'flex-end',
          '& > *:nth-child(2)': {
            '& > *': {
              justifyContent: 'flex-end',
            },
          },
        },
      },
    },
    isDescription: {
      true: {},
    },
  },
  compoundVariants: [
    {
      layout: 'two-column',
      isDescription: 'true',
      css: {
        display: 'grid',
        '@lg': {
          gridTemplateColumns: 'repeat(2, 1fr)',
          '& > *': {
            maxWidth: 'none !important',
          },
        },
      },
    },
    {
      layout: 'two-column',
      isDescription: 'false',
      css: {
        display: 'flex',
        flexDirection: 'column',
        '@lg': {
          flexDirection: 'row',
          justifyContent: 'space-between',
          '& > *:nth-child(1)': {
            flexGrow: 1,
            flexBasis: 0,
            maxWidth: '100%',
          },
          '& > *:nth-child(2)': {
            flexGrow: 0,
            flexBasis: 'max-content',
          },
        },
      },
    },
  ],
  defaultVariants: {
    layout: 'two-column',
  },
});

const HeadlineWrap = styled('div', {
  marginBottom: '$space-6',
  '@sm': {
    marginBottom: '$space-10',
  },
  variants: {
    hideLeadAndTitle: {
      true: {
        marginTop: '$space-6',
        marginBottom: '$space-0',
        '@sm': {
          marginTop: '$space-10',
        },
      },
    },
  },
});

export interface HeadingLevel2Props
  extends React.ComponentProps<typeof StyledHeadline> {
  /**
   * The class name for the component.
   */
  className?: string;

  /**
   * The CSS styles for the component.
   */
  css?: CSS;

  /**
   * The lead text for the component.
   */
  lead?: string;

  /**
   * The title text for the component.
   */
  title: string;

  /**
   * The description text for the component.
   */
  description?: string;

  /**
   * The features for the component.
   */
  features?: string[];

  /**
   * The remark text for the component.
   */
  remark?: string;

  /**
   * The buttons for the component.
   */
  buttons?: IButton[];

  colorLead?: 'red' | 'gray' | 'white' | 'blue';

  /**
   * Indicates if the theme is dark.
   */
  isDarkTheme?: boolean;

  /**
   * The layout of the component.
   */
  layout?: 'two-column' | 'two-row';

  /**
   * Indicates if the lead and title should be hidden.
   */
  hideLeadAndTitle?: boolean;

  /**
   * Indicates if the buttons should be hidden on mobile.
   */
  hideButtonOnMobile?: boolean;
  alignCenter?: boolean;

  /**
   * Indicates if the component should be aligned center on mobile.
   */
  alignCenterOnMobile?: boolean;

  /**
   * The data track for tracking.
   */
  dataTrack?: string;

  /**
   * The data track section for tracking.
   */
  dataTrackSection?: string;

  /**
   * The data track value for tracking.
   */
  dataTrackValue?: string;

  /**
   * The data test for testing.
   */
  dataTest?: string;
}

/**
 * Renders a HeadingLevel2 component with the provided props.
 *
 * @param {HeadingLevel2Props} props - The props for the HeadingLevel2 component.
 * @param {string} props.className - The class name for the component.
 * @param {CSS} props.css - The CSS styles for the component.
 * @param {string} props.lead - The lead text for the component.
 * @param {string} props.title - The title text for the component.
 * @param {string} props.description - The description text for the component.
 * @param {string[]} props.features - The features for the component.
 * @param {string} props.remark - The remark text for the component.
 * @param {IButton[]} props.buttons - The buttons for the component.
 * @param {boolean} props.isDarkTheme - Indicates if the theme is dark.
 * @param {'two-column' | 'two-row'} props.layout - The layout of the component.
 * @param {boolean} props.hideLeadAndTitle - Indicates if the lead and title should be hidden.
 * @param {boolean} props.hideButtonOnMobile - Indicates if the buttons should be hidden on mobile.
 * @param {boolean} props.alignCenterOnMobile - Indicates if the component should be aligned center on mobile.
 * @param {string} props.dataTrack - The data track for tracking.
 * @param {string} props.dataTrackSection - The data track section for tracking.
 * @param {string} props.dataTrackValue - The data track value for tracking.
 * @param {string} props.dataTest - The data test for testing.
 * @return {ReactElement} The rendered HeadingLevel2 component.
 */
export const HeadingLevel2: React.FC<HeadingLevel2Props> = ({
  className,
  css,
  lead,
  title,
  description,
  features,
  remark,
  buttons = [],
  colorLead,
  isDarkTheme = false,
  layout = 'two-column',
  hideLeadAndTitle = false,
  hideButtonOnMobile = false,
  alignCenter = false,
  alignCenterOnMobile = false,
  dataTrack,
  dataTrackSection,
  dataTrackValue,
  dataTest = 'heading',
  ...rest
}: HeadingLevel2Props) => {
  const getTitleColor = () =>
    isDarkTheme ? 'text-white' : 'text-gray-darkest';

  const getLeadVariant = () => {
    if (colorLead === 'red') {
      return 'text-red';
    }

    if (colorLead === 'gray') {
      return 'text-gray';
    }

    if (colorLead === 'blue') {
      return 'text-blue';
    }

    if (colorLead === 'white') {
      return 'text-white';
    }

    return isDarkTheme ? 'text-white' : 'text-red';
  };

  const hasButtons = buttons.some((button) => button.label !== '');

  const getAlign = () =>
    layout === 'two-column' && !description && hasButtons ? 'end' : 'start';

  const conditionalStyles = alignCenter
    ? {
        '@lg': {
          placeItems: 'center',
          textAlign: 'center',
        },
      }
    : alignCenterOnMobile
      ? {
          '@maxlg': {
            placeItems: 'center',
            textAlign: 'center',
          },
        }
      : {};

  const finalCss = {
    ...css,
    ...conditionalStyles,
  };

  return (
    <HeadlineWrap
      hideLeadAndTitle={!!hideLeadAndTitle && buttons.length > 0}
      css={
        hasButtons
          ? hideLeadAndTitle
            ? { '@lg': { display: 'none' } }
            : undefined
          : hideLeadAndTitle
            ? { display: 'none' }
            : undefined
      }
    >
      <Container>
        <StyledHeadline
          className={className}
          {...rest}
          layout={layout}
          align={getAlign()}
          isDescription={!!description}
          data-test={dataTest}
          css={finalCss}
        >
          {!hideLeadAndTitle && (
            <Grid>
              {!!lead && (
                <Text
                  as="h3"
                  size="text-body-1"
                  font="bold"
                  variant={getLeadVariant()}
                  wrap
                  dangerouslySetInnerHTML={{ __html: lead }}
                  data-test={`${dataTest}_preTitle`}
                  css={{ marginBottom: '$space-2' }}
                />
              )}
              {!!title && (
                <Text
                  as="h2"
                  size="text-h2"
                  variant={getTitleColor()}
                  wrap
                  dangerouslySetInnerHTML={{ __html: title }}
                  data-test={`${dataTest}_title`}
                />
              )}
              {!!features && (
                <Grid rowGap="2" css={{ marginTop: 24 }}>
                  {features.map((feature, index) => (
                    <Flex key={index} columnGap="2">
                      <Icon icon="correct" color="icon-red" />
                      <Text
                        size="text-body-1"
                        wrap
                        dangerouslySetInnerHTML={{
                          __html: feature,
                        }}
                      />
                    </Flex>
                  ))}
                </Grid>
              )}
            </Grid>
          )}
          {!!description && (
            <Grid rowGap="6">
              {!hideLeadAndTitle && !!description && (
                <Box>
                  <>
                    <Text
                      as="div"
                      size="text-body-1"
                      variant={getTitleColor()}
                      wrap
                      dangerouslySetInnerHTML={{
                        __html: description,
                      }}
                      data-test={`${dataTest}_description`}
                    />
                    {!!remark && (
                      <Text
                        as="div"
                        size="text-caption"
                        variant={getLeadVariant()}
                        wrap
                        dangerouslySetInnerHTML={{ __html: remark }}
                        data-test={`${dataTest}_remark`}
                        css={{ marginTop: '$space-2' }}
                      />
                    )}
                  </>
                </Box>
              )}
              {!!hasButtons && (
                <Box
                  css={
                    hideButtonOnMobile
                      ? {
                          '@maxlg': {
                            display: 'none',
                          },
                        }
                      : undefined
                  }
                >
                  <ButtonGroup
                    buttons={buttons}
                    variant="button-red"
                    isMinWidth
                    tracking={{
                      dataTrack: dataTrack ?? '',
                      dataTrackSection: dataTrackSection,
                      dataTrackValue: dataTrackValue,
                    }}
                    test={{
                      dataTest: `${dataTest}_button`,
                    }}
                  />
                </Box>
              )}
            </Grid>
          )}
          {!description && !!hasButtons && (
            <Box
              css={
                hideButtonOnMobile
                  ? {
                      '@maxlg': {
                        display: 'none',
                      },
                    }
                  : undefined
              }
            >
              <ButtonGroup
                buttons={buttons}
                variant="button-red"
                isMinWidth
                tracking={{
                  dataTrack: dataTrack ?? '',
                  dataTrackSection: dataTrackSection,
                  dataTrackValue: dataTrackValue,
                }}
                test={{
                  dataTest: `${dataTest}_button`,
                }}
              />
            </Box>
          )}
        </StyledHeadline>
      </Container>
    </HeadlineWrap>
  );
};

HeadingLevel2.displayName = 'HeadingLevel2';
